/**
 * RÉGLAGES › BILLETTERIE — le modèle pur (22/10/2026, contrat `soirees-v1` §7).
 *
 * Deux sources, jamais mélangées :
 *  · les réglages DE GESTION (`instance_setting['billetterie_soirees']`) : le
 *    gel, l'encart, l'entraînement, le rafraîchissement, le pré-remplissage de
 *    l'assistant et les MODÈLES DE TARIFS (§7.2). Ils ne servent qu'à
 *    pré-remplir : une fois la soirée créée, plus rien ne relie un tarif à son
 *    modèle ;
 *  · les réglages DE LA BILLETTERIE (§4.4), lus et écrits par son API, jamais
 *    copiés ici. Seules trois clés s'écrivent depuis Gestion ; les autres
 *    protègent la page de secours elle-même.
 */

import { CANAUX, GROUPE_RX, HEURE_RX, SALLE_RX, type Canal } from './soirees-maison-model.js'

/* ═══════════════════════ les modèles de tarifs (§7.2) ═══════════════════════ */

/** Une heure de vente RELATIVE à la soirée : l'assistant la pose sur la nuit choisie. */
export type HeureRelative =
  | null
  | { heure_de_nuit: string }
  | { a_l_ouverture: true }
  | { jours_avant: number; heure: string }

export type ChampASaisir = 'prix_cents' | 'quota'

export interface ModeleTarif {
  id: string
  libelle: string
  coche: boolean
  nom: string
  canal: Canal
  /** `null` seulement si `prix_cents` est dans `a_saisir` (une formule au prix « à saisir »). */
  prix_cents: number | null
  frais_service_cents: number
  /** `null` = le réglage `tarif.tva_bp_defaut` de la billetterie. */
  tva_bp: number | null
  quota: number | null
  a_saisir: ChampASaisir[]
  cascade_groupe: string | null
  cascade_rang: number | null
  entree_avant: string | null
  vente_debut: HeureRelative
  vente_fin: HeureRelative
  exoneration: boolean
  cache: boolean
  consigne_porte: string | null
}

const base = {
  coche: false, frais_service_cents: 0, tva_bp: null, quota: null, a_saisir: [] as ChampASaisir[],
  cascade_groupe: null, cascade_rang: null, entree_avant: null, vente_debut: null as HeureRelative, vente_fin: null as HeureRelative,
  exoneration: false, cache: false, consigne_porte: null,
}

/**
 * Les modèles proposés (§7.2), prix d'après Shotgun (120 derniers jours),
 * modifiables dans Réglages › Billetterie. Décisions du 22/10 (annexe C) :
 * TVA du réglage par défaut (5,5 %) partout, formules comprises, « à confirmer
 * par la compta » ; les tarifs SUR PLACE commencent leur vente À L'OUVERTURE.
 */
export const MODELES_DEFAUT: readonly ModeleTarif[] = [
  { ...base, id: 'prevente-prime', libelle: 'Prévente Prime', nom: 'PRIME (VALABLE AVANT 1H30)', canal: 'EN_LIGNE', prix_cents: 500, a_saisir: ['quota'], cascade_groupe: 'prevente', cascade_rang: 1, entree_avant: '01:30' },
  { ...base, id: 'prevente-regular', libelle: 'Prévente Regular', nom: 'REGULAR (VALABLE AVANT 2H30)', canal: 'EN_LIGNE', prix_cents: 700, a_saisir: ['quota'], cascade_groupe: 'prevente', cascade_rang: 2, entree_avant: '02:30' },
  { ...base, id: 'prevente-last', libelle: 'Prévente Last', nom: 'LAST', canal: 'EN_LIGNE', prix_cents: 1000, cascade_groupe: 'prevente', cascade_rang: 3 },
  { ...base, id: 'sur-place-avant-2h30', libelle: 'Sur place avant 2 h 30', coche: true, nom: 'SUR PLACE AVANT 2H30', canal: 'SUR_PLACE', prix_cents: 800, cascade_groupe: 'sur-place', cascade_rang: 1, vente_debut: { a_l_ouverture: true }, vente_fin: { heure_de_nuit: '02:30' } },
  { ...base, id: 'sur-place', libelle: 'Sur place', coche: true, nom: 'SUR PLACE', canal: 'SUR_PLACE', prix_cents: 1000, cascade_groupe: 'sur-place', cascade_rang: 2, vente_debut: { a_l_ouverture: true } },
  { ...base, id: 'etudiant', libelle: 'Étudiant', nom: 'ÉTUDIANT', canal: 'EN_LIGNE', prix_cents: 500, consigne_porte: 'Vérifier la carte étudiante' },
  { ...base, id: 'formule-essentiel', libelle: 'Formule Essentiel', nom: 'FORMULE ESSENTIEL', canal: 'EN_LIGNE', prix_cents: null, a_saisir: ['prix_cents'] },
  { ...base, id: 'formule-soft', libelle: 'Formule Soft', nom: 'FORMULE SOFT', canal: 'EN_LIGNE', prix_cents: null, a_saisir: ['prix_cents'] },
  { ...base, id: 'formule-prestige', libelle: 'Formule Prestige', nom: 'FORMULE PRESTIGE', canal: 'EN_LIGNE', prix_cents: null, a_saisir: ['prix_cents'] },
  { ...base, id: 'exoneration', libelle: 'Exonération', coche: true, nom: 'EXONÉRATION', canal: 'SUR_PLACE', prix_cents: 0, exoneration: true, vente_debut: { a_l_ouverture: true } },
]

export const MODELES_MAX = 30

/* ═══════════════════════ les réglages de Gestion (§7.1) ═══════════════════════ */

export type Rafraichissement = 15 | 30 | 60
export const RAFRAICHISSEMENTS: readonly Rafraichissement[] = [15, 30, 60]

export interface NouvelleSoireeDefaut { ouverture: string; fermeture: string; salles: string[]; jauge_totale: number | null }

export interface Preferences {
  ecritures_autorisees: boolean
  motif_gel: string | null
  /** Posés par le serveur au moment du gel, jamais envoyés : qui, et quand. */
  gele_par: string | null
  gele_a: string | null
  encart_demo: boolean
  entrainement_visible: boolean
  rafraichissement_s: Rafraichissement
  nouvelle_soiree: NouvelleSoireeDefaut
  modeles_tarifs: ModeleTarif[]
}

export const PREFERENCES_DEFAUT: Preferences = {
  ecritures_autorisees: true,
  motif_gel: null,
  gele_par: null,
  gele_a: null,
  encart_demo: true,
  entrainement_visible: false,
  rafraichissement_s: 30,
  nouvelle_soiree: { ouverture: '23:55', fermeture: '06:00', salles: ['R1', 'R2', 'R3'], jauge_totale: null },
  modeles_tarifs: MODELES_DEFAUT.map((m) => ({ ...m, a_saisir: [...m.a_saisir] })),
}

type Obj = Record<string, unknown>
const obj = (v: unknown): Obj | null => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : null)
const ent = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) ? v : null)
const texteOuNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null)

function lireHeureRelative(v: unknown): HeureRelative {
  const o = obj(v)
  if (!o) return null
  if (typeof o['heure_de_nuit'] === 'string' && HEURE_RX.test(o['heure_de_nuit'])) return { heure_de_nuit: o['heure_de_nuit'] }
  if (o['a_l_ouverture'] === true) return { a_l_ouverture: true }
  const j = ent(o['jours_avant'])
  if (j !== null && j >= 0 && j <= 90 && typeof o['heure'] === 'string' && HEURE_RX.test(o['heure'])) return { jours_avant: j, heure: o['heure'] }
  return null
}

function lireModele(v: unknown): ModeleTarif | null {
  const o = obj(v)
  if (!o) return null
  const id = texteOuNull(o['id']); const nom = texteOuNull(o['nom'])
  const canal = o['canal']
  if (!id || !nom || !(CANAUX as readonly unknown[]).includes(canal)) return null
  const aSaisir = (Array.isArray(o['a_saisir']) ? o['a_saisir'] : []).filter((x): x is ChampASaisir => x === 'prix_cents' || x === 'quota')
  return {
    id, libelle: texteOuNull(o['libelle']) ?? nom, coche: o['coche'] === true, nom, canal: canal as Canal,
    prix_cents: ent(o['prix_cents']), frais_service_cents: ent(o['frais_service_cents']) ?? 0, tva_bp: ent(o['tva_bp']),
    quota: ent(o['quota']), a_saisir: [...new Set(aSaisir)],
    cascade_groupe: texteOuNull(o['cascade_groupe']), cascade_rang: ent(o['cascade_rang']),
    entree_avant: typeof o['entree_avant'] === 'string' && HEURE_RX.test(o['entree_avant']) ? o['entree_avant'] : null,
    vente_debut: lireHeureRelative(o['vente_debut']), vente_fin: lireHeureRelative(o['vente_fin']),
    exoneration: o['exoneration'] === true, cache: o['cache'] === true, consigne_porte: texteOuNull(o['consigne_porte']),
  }
}

/**
 * Les réglages de Gestion tels que le serveur les rend — déjà normalisés par
 * lui (§7.1). On accepte `{ preferences: {…} }` ou l'objet seul ; chaque clé
 * absente prend son défaut. Le GEL fait exception, comme côté serveur : une
 * valeur présente mais pas booléenne vaut « gelé », jamais « autorisé ».
 */
export function lirePreferences(v: unknown): Preferences {
  const racine = obj(v)
  const o = obj(racine?.['preferences']) ?? racine ?? {}
  const d = PREFERENCES_DEFAUT
  const ns = obj(o['nouvelle_soiree'])
  const r = ent(o['rafraichissement_s'])
  const modelesBruts = Array.isArray(o['modeles_tarifs']) ? o['modeles_tarifs'] : null
  const modeles = modelesBruts === null ? d.modeles_tarifs.map((m) => ({ ...m })) : modelesBruts.map(lireModele).filter((m): m is ModeleTarif => m !== null)
  const gel = o['ecritures_autorisees']
  return {
    ecritures_autorisees: gel === undefined ? true : gel === true,
    motif_gel: texteOuNull(o['motif_gel']),
    gele_par: texteOuNull(o['gele_par']),
    gele_a: texteOuNull(o['gele_a']),
    encart_demo: typeof o['encart_demo'] === 'boolean' ? o['encart_demo'] : d.encart_demo,
    entrainement_visible: typeof o['entrainement_visible'] === 'boolean' ? o['entrainement_visible'] : d.entrainement_visible,
    rafraichissement_s: r === 15 || r === 30 || r === 60 ? r : d.rafraichissement_s,
    nouvelle_soiree: {
      ouverture: typeof ns?.['ouverture'] === 'string' && HEURE_RX.test(ns['ouverture']) ? ns['ouverture'] : d.nouvelle_soiree.ouverture,
      fermeture: typeof ns?.['fermeture'] === 'string' && HEURE_RX.test(ns['fermeture']) ? ns['fermeture'] : d.nouvelle_soiree.fermeture,
      salles: Array.isArray(ns?.['salles']) ? (ns['salles'] as unknown[]).filter((x): x is string => typeof x === 'string' && SALLE_RX.test(x)) : [...d.nouvelle_soiree.salles],
      jauge_totale: ns && 'jauge_totale' in ns ? ent(ns['jauge_totale']) : d.nouvelle_soiree.jauge_totale,
    },
    modeles_tarifs: modeles,
  }
}

export type CorpsPreferences = Omit<Preferences, 'gele_par' | 'gele_a'>

/**
 * L'objet envoyé à `PUT /api/v1/billetterie/preferences` : les clés du §7.1,
 * rien d'autre — qui a gelé, et quand, ne vient jamais du navigateur.
 */
export function corpsPreferences(p: Preferences): CorpsPreferences {
  return {
    ecritures_autorisees: p.ecritures_autorisees,
    motif_gel: p.ecritures_autorisees ? null : (p.motif_gel?.trim() || null),
    encart_demo: p.encart_demo,
    entrainement_visible: p.entrainement_visible,
    rafraichissement_s: p.rafraichissement_s,
    nouvelle_soiree: { ...p.nouvelle_soiree, salles: [...p.nouvelle_soiree.salles] },
    modeles_tarifs: p.modeles_tarifs.map((m) => ({
      ...m, nom: m.nom.trim(), libelle: m.libelle.trim(), consigne_porte: m.consigne_porte?.trim() || null,
      cascade_groupe: m.cascade_groupe?.trim() || null, a_saisir: [...m.a_saisir],
      prix_cents: m.a_saisir.includes('prix_cents') ? null : m.prix_cents,
      quota: m.a_saisir.includes('quota') ? null : m.quota,
    })),
  }
}

/** Un JSON aux clés triées : deux valeurs égales s'écrivent pareil, quel que soit l'ordre de leurs clés. */
function jsonCanonique(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(jsonCanonique).join(',')}]`
  const o = obj(v)
  if (!o) return JSON.stringify(v) ?? 'null'
  return `{${Object.keys(o).sort().map((k) => `${JSON.stringify(k)}:${jsonCanonique(o[k])}`).join(',')}}`
}

/** Nombre de modifications à afficher dans la Save Bar (une par clé, une par modèle). */
export function changementsPreferences(lu: Preferences, brouillon: Preferences): number {
  const a = corpsPreferences(lu); const b = corpsPreferences(brouillon)
  let n = 0
  for (const k of ['ecritures_autorisees', 'motif_gel', 'encart_demo', 'entrainement_visible', 'rafraichissement_s'] as const) {
    if (a[k] !== b[k]) n++
  }
  if (jsonCanonique(a.nouvelle_soiree) !== jsonCanonique(b.nouvelle_soiree)) n++
  const ids = new Set([...a.modeles_tarifs.map((m) => m.id), ...b.modeles_tarifs.map((m) => m.id)])
  for (const id of ids) {
    const x = a.modeles_tarifs.find((m) => m.id === id); const y = b.modeles_tarifs.find((m) => m.id === id)
    if (jsonCanonique(x) !== jsonCanonique(y)) n++
  }
  if (n === 0 && a.modeles_tarifs.map((m) => m.id).join() !== b.modeles_tarifs.map((m) => m.id).join()) n++
  return n
}

/**
 * Ce qui part à `PUT /api/v1/billetterie/preferences` : SEULEMENT les clés que
 * la personne a changées depuis la lecture (le serveur accepte un correctif
 * partiel). Le GEL et son motif ne partent que si elle y a touché : un onglet
 * ouvert avant un gel posé ailleurs ne le lève jamais en enregistrant autre
 * chose. Seule exception : un gel ILLISIBLE se réécrit tel qu'affiché, gelé.
 */
export function correctifPreferences(lu: Preferences, brouillon: Preferences, o: { gelIllisible?: boolean } = {}): Partial<CorpsPreferences> {
  const a = corpsPreferences(lu); const b = corpsPreferences(brouillon)
  const out: Partial<CorpsPreferences> = {}
  for (const k of Object.keys(b) as (keyof CorpsPreferences)[]) {
    if (jsonCanonique(a[k]) !== jsonCanonique(b[k])) (out as Record<string, unknown>)[k] = b[k]
  }
  // Geler ou dégeler emporte le motif (effacé au dégel, posé au gel).
  if ('ecritures_autorisees' in out) out.motif_gel = b.motif_gel
  if (o.gelIllisible && !('ecritures_autorisees' in out)) out.ecritures_autorisees = b.ecritures_autorisees
  return out
}

/** Contrôles de forme AVANT l'envoi (le serveur reste juge) ; `null` si tout va bien. */
export function preferencesInvalides(p: Preferences): string | null {
  if (!p.ecritures_autorisees && (p.motif_gel?.trim().length ?? 0) > 200) return 'Motif du gel : 200 caractères au plus.'
  const ns = p.nouvelle_soiree
  if (!HEURE_RX.test(ns.ouverture) || !HEURE_RX.test(ns.fermeture)) return 'Nouvelle soirée : heures d’ouverture et de fermeture au format HH:MM.'
  if (ns.jauge_totale !== null && (ns.jauge_totale < 1 || ns.jauge_totale > 20_000)) return 'Nouvelle soirée : la jauge proposée va de 1 à 20 000, ou reste à saisir.'
  if (p.modeles_tarifs.length > MODELES_MAX) return `Modèles de tarifs : ${MODELES_MAX} au plus.`
  const ids = new Set<string>()
  for (const [i, m] of p.modeles_tarifs.entries()) {
    const ou = `Modèle ${i + 1} (« ${m.libelle || m.nom || 'sans nom'} »)`
    if (!/^[a-z0-9][a-z0-9-]{0,39}$/.test(m.id)) return `${ou} : identifiant illisible.`
    if (ids.has(m.id)) return `${ou} : un autre modèle porte déjà cet identifiant.`
    ids.add(m.id)
    if (!m.libelle.trim() || m.libelle.trim().length > 60) return `${ou} : un libellé de 1 à 60 caractères.`
    if (!m.nom.trim() || m.nom.trim().length > 80) return `${ou} : un nom de tarif de 1 à 80 caractères.`
    if (!m.a_saisir.includes('prix_cents') && (m.prix_cents === null || m.prix_cents < 0 || m.prix_cents > 1_000_000)) {
      return `${ou} : un prix, ou « à saisir dans l’assistant ».`
    }
    if (!m.a_saisir.includes('quota') && m.quota !== null && (m.quota < 1 || m.quota > 20_000)) return `${ou} : un quota de 1 à 20 000, ou aucun.`
    if ((m.cascade_groupe === null) !== (m.cascade_rang === null)) return `${ou} : une cascade demande un groupe ET un rang.`
    if (m.cascade_groupe !== null && !GROUPE_RX.test(m.cascade_groupe)) return `${ou} : le groupe de cascade s’écrit en minuscules, chiffres et tirets.`
    if (m.cascade_rang !== null && (m.cascade_rang < 1 || m.cascade_rang > 50)) return `${ou} : un rang de cascade de 1 à 50.`
    if (m.exoneration && (m.canal !== 'SUR_PLACE' || (m.prix_cents ?? 0) > 0 || m.frais_service_cents > 0)) {
      return `${ou} : une exonération est gratuite, sans frais, et sur place.`
    }
    if (m.canal === 'INVITATION' && (m.prix_cents ?? 0) > 0) return `${ou} : une invitation est gratuite.`
    if (m.canal === 'SUR_PLACE' && m.frais_service_cents > 0) return `${ou} : pas de frais de service sur place.`
    if ((m.consigne_porte?.length ?? 0) > 80) return `${ou} : la consigne de porte tient en 80 caractères.`
  }
  return null
}

/**
 * §7.2 : à partir de la livraison 3, seule une formule dont le nom contient
 * Essentiel, Soft ou Prestige HORS PARENTHÈSES sera comptée pour le bar. Dit
 * au futur, sur l'écran des modèles.
 */
export function avertissementFormule(m: Pick<ModeleTarif, 'nom' | 'libelle' | 'id'>): string | null {
  const parle = /formule/i.test(`${m.nom} ${m.libelle} ${m.id}`)
  if (!parle) return null
  const horsParentheses = m.nom.replace(/\([^)]*\)/g, ' ')
  if (/essentiel|soft|prestige/i.test(horsParentheses)) return null
  return `À partir de la livraison 3, « ${m.nom.trim() || m.libelle} » ne serait pas reconnue comme formule : le nom doit contenir Essentiel, Soft ou Prestige, hors parenthèses.`
}

/** Les formules avec boissons comprises : leur taux est à faire valider (annexe C, point 4). */
export function tvaAConfirmer(m: Pick<ModeleTarif, 'nom' | 'id'>): boolean {
  return /formule/i.test(`${m.nom} ${m.id}`)
}

export function modeleVierge(existants: readonly { id: string }[]): ModeleTarif {
  let n = existants.length + 1
  while (existants.some((m) => m.id === `modele-${n}`)) n++
  return { ...base, a_saisir: [], id: `modele-${n}`, libelle: 'Nouveau modèle', nom: 'NOUVEAU TARIF', canal: 'SUR_PLACE', prix_cents: 1000 }
}

/** « Début de vente : à l'ouverture » — le sens d'une heure relative, en mots. */
export function heureRelativeLisible(h: HeureRelative): string {
  if (h === null) return 'aucune'
  if ('heure_de_nuit' in h) return `${h.heure_de_nuit} dans la nuit`
  if ('a_l_ouverture' in h) return 'à l’ouverture de la soirée'
  return h.jours_avant === 0 ? `le jour de la nuit à ${h.heure}` : `${h.jours_avant} jour${h.jours_avant > 1 ? 's' : ''} avant la nuit, à ${h.heure}`
}

/* ═══════════════════════ les réglages de la billetterie (§4.4) ═══════════════════════ */

export interface ReglageBilletterie {
  cle: string; libelle: string; valeur: number; defaut: number; min: number; max: number
  modifiable: boolean; raison: string | null; maj_a: string | null
}

/** Les trois seules clés que Gestion écrit (§4.12) ; toute autre est refusée avant l'appel. */
export const CLES_ECRITES_PAR_GESTION = ['tarif.tva_bp_defaut', 'porte.tolerance_horaire_min', 'porte.alerte_jauge_pct'] as const

/** Un libellé sans clé technique ni « (1 = oui, 0 = non) » : la conséquence d'abord. */
export const LIBELLE_REGLAGE: Record<string, { titre: string; aide: string; unite: string }> = {
  'tarif.tva_bp_defaut': { titre: 'TVA par défaut d’un nouveau tarif', aide: 'Prise par chaque tarif créé sans taux, puis figée au premier billet émis. 5,5 % pour l’entrée ; les formules avec boissons sont à confirmer par la compta.', unite: '%' },
  'porte.tolerance_horaire_min': { titre: 'Tolérance sur les heures limites d’entrée', aide: 'Un billet « valable avant 1 h 30 » passe encore pendant ces minutes de grâce.', unite: 'min' },
  'porte.alerte_jauge_pct': { titre: 'Alerte de jauge sur les téléphones de la porte', aide: 'Les téléphones LC Porte préviennent quand la salle approche de sa jauge totale. Appliqué à leur prochaine synchronisation.', unite: '%' },
  'caisse.motif_exoneration_obligatoire': { titre: 'Motif obligatoire pour une exonération', aide: 'Le contrôle anti-fraude de la caisse.', unite: '' },
  'secours.duree_session_h': { titre: 'Durée d’une session de la page de secours', aide: '', unite: 'h' },
  'secours.inactivite_min': { titre: 'Déconnexion de la page de secours après inactivité', aide: '', unite: 'min' },
  'gestion.soirees_autorisees': { titre: 'Gestion peut gérer les soirées', aide: 'La coupure posée côté billetterie : à « non », Gestion ne lit ni ne modifie plus aucune soirée, sans toucher à LC Porte.', unite: '' },
  'nuit.heure_bascule': { titre: 'Heure de bascule de la nuit', aide: 'Avant 8 h du matin, on est encore la nuit de la veille — comme partout dans Gestion.', unite: 'h' },
}

export function lireReglages(v: unknown): ReglageBilletterie[] | null {
  const o = obj(v)
  if (!o || !Array.isArray(o['reglages'])) return null
  const out: ReglageBilletterie[] = []
  for (const r of o['reglages'] as unknown[]) {
    const x = obj(r)
    const cle = typeof x?.['cle'] === 'string' ? x['cle'] : null
    const valeur = ent(x?.['valeur'])
    if (!x || !cle || valeur === null) continue
    out.push({
      cle, libelle: typeof x['libelle'] === 'string' ? x['libelle'] : cle, valeur,
      defaut: ent(x['defaut']) ?? valeur, min: ent(x['min']) ?? 0, max: ent(x['max']) ?? valeur,
      modifiable: x['modifiable'] === true && (CLES_ECRITES_PAR_GESTION as readonly string[]).includes(cle),
      raison: texteOuNull(x['raison']), maj_a: texteOuNull(x['maj_a']),
    })
  }
  return out
}

/** Le taux que prendra un tarif créé sans taux (`tarif.tva_bp_defaut`), ou `null` s'il n'est pas lu. */
export function tvaDefautDe(reglages: readonly Pick<ReglageBilletterie, 'cle' | 'valeur'>[]): number | null {
  return reglages.find((r) => r.cle === 'tarif.tva_bp_defaut')?.valeur ?? null
}

/** Une valeur affichée (la TVA en %, le reste tel quel). */
export function valeurAffichee(r: Pick<ReglageBilletterie, 'cle' | 'valeur'>): string {
  if (r.cle === 'tarif.tva_bp_defaut') return (r.valeur / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2, useGrouping: false })
  if (r.cle === 'caisse.motif_exoneration_obligatoire' || r.cle === 'gestion.soirees_autorisees') return r.valeur === 1 ? 'Oui' : 'Non'
  return String(r.valeur)
}

/** La saisie → la valeur du contrat (la TVA repasse en points de base) ; `undefined` si illisible. */
export function valeurSaisie(cle: string, saisie: string): number | undefined {
  const t = saisie.trim().replace(/\s|%/g, '')
  if (cle === 'tarif.tva_bp_defaut') {
    const m = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(t)
    return m ? Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0')) : undefined
  }
  return /^\d{1,5}$/.test(t) ? Number(t) : undefined
}

export interface ChangementReglage { cle: string; valeur: number; valeur_attendue: number }

/**
 * Le corps de `PUT /reglages` : SEULEMENT les clés réellement modifiées
 * (§4.12), chacune avec la valeur LUE en `valeur_attendue` (défaut compris).
 * Une saisie illisible ou hors bornes rend `{ erreur }` : rien ne part.
 */
export function changementsReglages(lus: readonly ReglageBilletterie[], saisies: Readonly<Record<string, string>>): { changements: ChangementReglage[] } | { erreur: string } {
  const changements: ChangementReglage[] = []
  for (const r of lus) {
    if (!r.modifiable) continue
    const s = saisies[r.cle]
    if (s === undefined || s.trim() === valeurAffichee(r)) continue
    const v = valeurSaisie(r.cle, s)
    const titre = LIBELLE_REGLAGE[r.cle]?.titre ?? r.libelle
    if (v === undefined) return { erreur: `${titre} : valeur illisible.` }
    if (v < r.min || v > r.max) {
      const borne = (x: number) => valeurAffichee({ cle: r.cle, valeur: x })
      return { erreur: `${titre} : entre ${borne(r.min)} et ${borne(r.max)}.` }
    }
    if (v !== r.valeur) changements.push({ cle: r.cle, valeur: v, valeur_attendue: r.valeur })
  }
  return { changements }
}

/* ═══════════════════════ la saisie d'un modèle (écran des réglages) ═══════════════════════
   Les nombres se tapent en texte (« 8, » est une étape de « 8,50 ») : la saisie
   garde le texte, la conversion ne se fait qu'à la comparaison et à l'envoi. */

export type GenreHeure = 'aucune' | 'ouverture' | 'nuit' | 'jours'
export interface HeureSaisie { genre: GenreHeure; heure: string; jours: string }

export interface ModeleSaisie {
  id: string; libelle: string; coche: boolean; nom: string; canal: Canal
  prix: string; prix_a_saisir: boolean; frais: string; tva: string; quota: string; quota_a_saisir: boolean
  cascade_groupe: string; cascade_rang: string; entree_avant: string
  vente_debut: HeureSaisie; vente_fin: HeureSaisie
  exoneration: boolean; cache: boolean; consigne_porte: string
}

const euroTexte = (c: number | null): string => (c === null ? '' : `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`)
const bpTexte = (bp: number | null): string => (bp === null ? '' : (bp / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2, useGrouping: false }))

function heureSaisie(h: HeureRelative): HeureSaisie {
  if (h === null) return { genre: 'aucune', heure: '', jours: '' }
  if ('heure_de_nuit' in h) return { genre: 'nuit', heure: h.heure_de_nuit, jours: '' }
  if ('a_l_ouverture' in h) return { genre: 'ouverture', heure: '', jours: '' }
  return { genre: 'jours', heure: h.heure, jours: String(h.jours_avant) }
}

export function saisieModele(m: ModeleTarif): ModeleSaisie {
  return {
    id: m.id, libelle: m.libelle, coche: m.coche, nom: m.nom, canal: m.canal,
    prix: euroTexte(m.prix_cents), prix_a_saisir: m.a_saisir.includes('prix_cents'),
    frais: m.frais_service_cents ? euroTexte(m.frais_service_cents) : '', tva: bpTexte(m.tva_bp),
    quota: m.quota === null ? '' : String(m.quota), quota_a_saisir: m.a_saisir.includes('quota'),
    cascade_groupe: m.cascade_groupe ?? '', cascade_rang: m.cascade_rang === null ? '' : String(m.cascade_rang),
    entree_avant: m.entree_avant ?? '', vente_debut: heureSaisie(m.vente_debut), vente_fin: heureSaisie(m.vente_fin),
    exoneration: m.exoneration, cache: m.cache, consigne_porte: m.consigne_porte ?? '',
  }
}

function centsTexte(v: string): number | null | undefined {
  const t = v.trim().replace(/\s|€/g, '')
  if (t === '') return null
  const m = /^(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(t)
  return m ? Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0')) : undefined
}

function heureRelative(h: HeureSaisie): HeureRelative | undefined {
  if (h.genre === 'aucune') return null
  if (h.genre === 'ouverture') return { a_l_ouverture: true }
  if (!HEURE_RX.test(h.heure)) return undefined
  if (h.genre === 'nuit') return { heure_de_nuit: h.heure }
  const j = Number(h.jours.trim())
  return /^\d{1,2}$/.test(h.jours.trim()) && j <= 90 ? { jours_avant: j, heure: h.heure } : undefined
}

/** La saisie → le modèle du contrat (§7.2), ou ce qui ne se lit pas. */
export function modeleDepuisSaisie(s: ModeleSaisie, rang: number): { modele: ModeleTarif } | { erreur: string } {
  const ou = `Modèle ${rang + 1} (« ${s.libelle.trim() || s.nom.trim() || 'sans nom'} »)`
  const prix = s.prix_a_saisir ? null : centsTexte(s.prix)
  if (prix === undefined || (!s.prix_a_saisir && prix === null)) return { erreur: `${ou} : un prix lisible (par exemple « 8 » ou « 8,50 »), ou « prix à saisir ».` }
  const frais = s.canal === 'EN_LIGNE' ? centsTexte(s.frais) : 0
  if (frais === undefined) return { erreur: `${ou} : frais illisibles.` }
  const t = s.tva.trim().replace(/\s|%/g, '')
  const mt = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(t)
  const tva = t === '' ? null : mt ? Number(mt[1]) * 100 + Number((mt[2] ?? '').padEnd(2, '0')) : undefined
  if (tva === undefined || (tva !== null && tva > 10_000)) return { erreur: `${ou} : TVA illisible (par exemple « 5,5 »), ou vide pour le réglage par défaut.` }
  const q = s.quota.trim()
  const quota = s.quota_a_saisir || q === '' ? null : /^\d{1,5}$/.test(q) ? Number(q) : undefined
  if (quota === undefined) return { erreur: `${ou} : quota illisible.` }
  const r = s.cascade_rang.trim()
  const rangCascade = r === '' ? null : /^\d{1,2}$/.test(r) ? Number(r) : undefined
  if (rangCascade === undefined) return { erreur: `${ou} : rang de cascade illisible.` }
  if (s.entree_avant && !HEURE_RX.test(s.entree_avant)) return { erreur: `${ou} : heure limite d’entrée illisible.` }
  const debut = heureRelative(s.vente_debut); const fin = heureRelative(s.vente_fin)
  if (debut === undefined) return { erreur: `${ou} : début de vente incomplet (heure, ou nombre de jours de 0 à 90).` }
  if (fin === undefined) return { erreur: `${ou} : fin de vente incomplète (heure, ou nombre de jours de 0 à 90).` }
  return {
    modele: {
      id: s.id, libelle: s.libelle.trim(), coche: s.coche, nom: s.nom.trim(), canal: s.canal,
      prix_cents: prix, frais_service_cents: frais ?? 0, tva_bp: tva, quota,
      a_saisir: [...(s.prix_a_saisir ? ['prix_cents' as const] : []), ...(s.quota_a_saisir ? ['quota' as const] : [])],
      cascade_groupe: s.cascade_groupe.trim() || null, cascade_rang: rangCascade, entree_avant: s.entree_avant || null,
      vente_debut: debut, vente_fin: fin,
      exoneration: s.canal === 'SUR_PLACE' ? s.exoneration : false, cache: s.cache, consigne_porte: s.consigne_porte.trim() || null,
    },
  }
}

/** Toutes les saisies → les modèles, ou la première erreur. */
export function modelesDepuisSaisies(l: readonly ModeleSaisie[]): { modeles: ModeleTarif[] } | { erreur: string } {
  const modeles: ModeleTarif[] = []
  for (const [i, s] of l.entries()) {
    const r = modeleDepuisSaisie(s, i)
    if ('erreur' in r) return r
    modeles.push(r.modele)
  }
  return { modeles }
}
